import React from "react";
import { CheckCircle2, LogOut, X } from "lucide-react";

const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

* {
  box-sizing: border-box;
}

.signout-overlay {
  position: fixed;
  inset: 0;
  background: rgba(8, 20, 38, 0.66);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1200;
  backdrop-filter: blur(8px);
  animation: modal-fade 0.28s ease forwards;
}

.signout-modal {
  width: min(480px, 94vw);
  background: #ffffff;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 30px 90px rgba(7, 24, 47, 0.3);
  border: 1px solid rgba(148, 163, 184, 0.18);
  animation: modal-pop 0.3s cubic-bezier(.16,1,.3,1) forwards;
}

.signout-head {
  padding: 28px 28px 0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
}

.signout-title {
  font-size: 23px;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.12;
}

.signout-close {
  width: 36px;
  height: 36px;
  border-radius: 11px;
  border: 1px solid rgba(148, 163, 184, 0.28);
  background: rgba(248, 250, 252, 0.95);
  color: #475569;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.25s ease, background 0.25s ease, color 0.25s ease;
}

.signout-close:hover {
  transform: scale(1.05);
  background: #e2e8f0;
  color: #0f172a;
}

.signout-body {
  padding: 18px 28px 28px;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.signout-text {
  color: #475569;
  font-size: 14px;
  line-height: 1.65;
}

.signout-intro {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}

.signout-icon {
  width: 46px;
  height: 46px;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  border-radius: 14px;
  background: linear-gradient(145deg, #e0f2fe, #dbeafe);
  color: #2563eb;
  box-shadow: inset 0 0 0 1px #bfdbfe;
}

.signout-kicker {
  margin-bottom: 7px;
  color: #2563eb;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: .12em;
  text-transform: uppercase;
}

.signout-status {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 13px 14px;
  border: 1px solid #dbeafe;
  border-radius: 14px;
  background: linear-gradient(110deg, #f8fbff, #eff6ff);
  color: #334155;
  font-size: 12px;
  font-weight: 600;
}

.signout-status svg {
  flex-shrink: 0;
  color: #16a34a;
}

.signout-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 2px;
}

.signout-btn-secondary {
  min-width: 116px;
  border-radius: 11px;
  border: 1px solid #cbd5e1;
  background: #f8fafc;
  color: #475569;
  font-weight: 700;
  padding: 12px 18px;
  cursor: pointer;
  transition: transform 0.25s ease, background 0.25s ease, color 0.25s ease;
}

.signout-btn-secondary:hover {
  transform: translateY(-1px);
  background: #edf2f7;
}

.signout-btn-primary {
  min-width: 132px;
  border-radius: 11px;
  border: none;
  background: #dc2626;
  color: #fff;
  font-weight: 700;
  padding: 12px 18px;
  cursor: pointer;
  transition: transform 0.25s ease, box-shadow 0.25s ease, background 0.25s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  box-shadow: 0 10px 22px rgba(220, 38, 38, 0.22);
}

.signout-btn-primary:hover {
  transform: translateY(-1px);
  background: #b91c1c;
  color: #fff;
}

.signout-btn-primary:disabled {
  opacity: 0.64;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

@keyframes modal-pop {
  from {
    opacity: 0;
    transform: translateY(16px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes modal-fade {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@media (max-width: 540px) {
  .signout-modal {
    width: 100%;
    border-radius: 24px;
  }

  .signout-head {
    padding: 22px 20px 0;
  }

  .signout-body {
    padding: 18px 20px 22px;
  }

  .signout-actions > button {
    flex: 1;
  }
}
`;

export default function SignOutConfirmation({ open, onCancel, onConfirm }) {
  if (!open) {
    return null;
  }

  return (
    <>
      <style>{STYLES}</style>
      <div className="signout-overlay" onClick={onCancel}>
        <div className="signout-modal" onClick={(event) => event.stopPropagation()}>
          <div className="signout-head">
            <div className="signout-intro">
              <span className="signout-icon"><LogOut size={21} /></span>
              <div>
              <div className="signout-kicker">Account session</div>
              <div className="signout-title">Confirm Sign Out</div>
              <div className="signout-text">
                Are you sure you want to sign out from the clinic dashboard?
              </div>
              </div>
            </div>
            <button className="signout-close" onClick={onCancel} aria-label="Close">
              <X size={18} />
            </button>
          </div>

          <div className="signout-body">
            <div className="signout-status">
              <CheckCircle2 size={18} />
              <span>Your clinic session will close securely.</span>
            </div>

            <div className="signout-actions">
              <button className="signout-btn-secondary" onClick={onCancel}>
                Cancel
              </button>
              <button className="signout-btn-primary" onClick={onConfirm}>
                Sign out
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
